import React from 'react';
import {
    FlexDiv,
    Block,
    Heading4,
    TextDefault,
    TextInput,
} from './ui';
import {
    Checkbox,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    TextField,
    Stack,
} from '@mui/material';
import { FlatBlockScoresSection } from './flatBlockScoresSection';
import { ConversationBlockEditor } from './conversationBlockEditor';
import { YesNoListBlockEditor } from './yesNoListBlockEditor';
import { PasswordTesterBlockEditor } from './passwordTesterBlockEditor';
import { DecisionBlockEditor } from './decisionBlockEditor';
import { GroupOfBlocksBlockEditor } from './groupOfBlocksBlockEditor';
import { BlockHiddenBlockEditor } from './blockHiddenBlockEditor';
import { CustomPresentationEditor } from './customPresentationEditor';
import {
    collectPresentationActionIds,
    getArrayActionComponent,
    getArrayActionScore,
    isArrayActionEntry,
    setArrayActionComponent,
    setArrayActionScore,
} from './questionActionsEditor';
import { ScoringComponentSelect } from './scoringComponentSelect';
import {
    questionComponentsSelectValue,
    scoringComponentSelectOptions,
} from './threatAreaScoring';

/**
 * Values for the Threat Area select: company catalog (`threatAreaName`) plus
 * the question's current `area` when it is not in the catalog (legacy / import).
 */
function threatAreaSelectValues(threatAreaOptions, currentArea) {
    const out = [];
    const seen = new Set();
    const add = (v) => {
        if (v == null) return;
        const s = String(v).trim();
        if (!s || seen.has(s)) return;
        seen.add(s);
        out.push(s);
    };
    if (Array.isArray(threatAreaOptions)) {
        for (const o of threatAreaOptions) {
            if (typeof o === 'string') add(o);
            else if (o?.threatAreaName) add(o.threatAreaName);
        }
    }
    add(currentArea);
    if (out.length === 0) {
        add('General');
    }
    return out;
}

function PropertiesPanel({
    variant = 'full',
    selectedBlock,
    selectedPresentation,
    question,
    /** Company threat areas from admin (`catalogStore.availableThreatAreas`). */
    threatAreaOptions,
    /** Full threat area rows with `keyBehaviours` (`catalogStore.threatAreaServerData`). */
    threatAreasCatalog,
    presentationTypes,
    onChangePresentationType,
    onPatchQuestion,
    onUpdateBlock,
    onUpdatePresentation,
    onUpdateQuestion,
}) {
    const showQuestion = variant === 'full' || variant === 'question-only';
    const showSelection =
        variant === 'full' || variant === 'selection-only';

    const scoringComponentOptions = scoringComponentSelectOptions(
        threatAreasCatalog,
        question?.area
    );

    const renderBlockProperties = () => {
        if (!selectedBlock) return null;

        return (
            <Block padding={16} gap={16}>
                <Heading4>Block Properties</Heading4>
                
                <FlexDiv column gap={12}>
                    <TextDefault bold>Type: {selectedBlock.type}</TextDefault>
                    
                    <TextInput
                        label="Title"
                        value={selectedBlock.title || ''}
                        onChange={(e) => onUpdateBlock({ title: e.target.value })}
                        placeholder="Block title"
                    />

                    <ConversationBlockEditor
                        selectedBlock={selectedBlock}
                        question={question}
                        scoringComponentOptions={scoringComponentOptions}
                        onPatchQuestion={onPatchQuestion}
                        onUpdateBlock={onUpdateBlock}
                    />

                    <FlatBlockScoresSection
                        selectedBlock={selectedBlock}
                        question={question}
                        scoringComponentOptions={scoringComponentOptions}
                        onPatchQuestion={onPatchQuestion}
                        onUpdateBlock={onUpdateBlock}
                    />

                    <PasswordTesterBlockEditor
                        selectedBlock={selectedBlock}
                        question={question}
                        scoringComponentOptions={scoringComponentOptions}
                        onPatchQuestion={onPatchQuestion}
                        onUpdateBlock={onUpdateBlock}
                    />

                    <DecisionBlockEditor
                        selectedBlock={selectedBlock}
                        question={question}
                        scoringComponentOptions={scoringComponentOptions}
                        onPatchQuestion={onPatchQuestion}
                        onUpdateBlock={onUpdateBlock}
                    />

                    <GroupOfBlocksBlockEditor
                        selectedBlock={selectedBlock}
                        question={question}
                        scoringComponentOptions={scoringComponentOptions}
                        onPatchQuestion={onPatchQuestion}
                        onUpdateBlock={onUpdateBlock}
                    />

                    <BlockHiddenBlockEditor
                        selectedBlock={selectedBlock}
                        onUpdateBlock={onUpdateBlock}
                    />

                    {selectedBlock.type === 'numericalscale' ? (
                        <FlexDiv column gap={8}>
                            <FlexDiv alignCenter gap={12}>
                                <TextInput
                                    label="Min Value"
                                    type="number"
                                    value={selectedBlock.min || 1}
                                    onChange={(e) => onUpdateBlock({ min: parseInt(e.target.value) })}
                                />
                                <TextInput
                                    label="Max Value"
                                    type="number"
                                    value={selectedBlock.max || 10}
                                    onChange={(e) => onUpdateBlock({ max: parseInt(e.target.value) })}
                                />
                            </FlexDiv>
                        </FlexDiv>
                    ) : null}

                    {selectedBlock.type === 'yes-no-list' ? (
                        <YesNoListBlockEditor
                            selectedBlock={selectedBlock}
                            question={question}
                            scoringComponentOptions={scoringComponentOptions}
                            onPatchQuestion={onPatchQuestion}
                            onUpdateBlock={onUpdateBlock}
                        />
                    ) : null}

                    {selectedBlock.type === 'questionwithimage' ? (
                        <FlexDiv column gap={8}>
                            <TextInput
                                label="Image URL"
                                value={selectedBlock.imageUrl || ''}
                                onChange={(e) => onUpdateBlock({ imageUrl: e.target.value })}
                                placeholder="https://example.com/image.jpg"
                            />
                        </FlexDiv>
                    ) : null}

                </FlexDiv>
            </Block>
        );
    };

    const renderPresentationProperties = () => {
        if (!selectedPresentation) return null;

        return (
            <Block padding={16} gap={16}>
                <Heading4>Presentation Properties</Heading4>

                <FlexDiv column gap={12}>
                    {presentationTypes && onChangePresentationType ? (
                        <FormControl fullWidth size="small">
                            <InputLabel id="presentation-type-label">
                                Presentation type
                            </InputLabel>
                            <Select
                                labelId="presentation-type-label"
                                label="Presentation type"
                                value={selectedPresentation.type || ''}
                                onChange={(e) =>
                                    onChangePresentationType(e.target.value)
                                }
                            >
                                {Object.keys(presentationTypes).map((key) => (
                                    <MenuItem key={key} value={key}>
                                        {presentationTypes[key]?.name || key}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <TextDefault bold>
                            Type: {selectedPresentation.type}
                        </TextDefault>
                    )}

                    <TextInput
                        label="Title"
                        value={selectedPresentation.title || ''}
                        onChange={(e) => onUpdatePresentation({ title: e.target.value })}
                        placeholder="Presentation title"
                    />

                    {selectedPresentation.type === 'email' ? (
                        <FlexDiv column gap={8}>
                            <TextInput
                                label="Subject"
                                value={selectedPresentation.subject || ''}
                                onChange={(e) => onUpdatePresentation({ subject: e.target.value })}
                                placeholder="Email subject"
                            />
                            <TextInput
                                label="Sender"
                                value={selectedPresentation.sender || ''}
                                onChange={(e) => onUpdatePresentation({ sender: e.target.value })}
                                placeholder="sender@example.com"
                            />
                            <TextField
                                multiline
                                rows={4}
                                fullWidth
                                label="Content"
                                value={selectedPresentation.content || ''}
                                onChange={(e) => onUpdatePresentation({ content: e.target.value })}
                                placeholder="Email content"
                            />
                        </FlexDiv>
                    ) : null}

                    {selectedPresentation.type === 'message' || selectedPresentation.type === 'password' || selectedPresentation.type === 'question' ? (
                        <TextField
                            multiline
                            rows={4}
                            fullWidth
                            label="Content"
                            value={selectedPresentation.content || ''}
                            onChange={(e) => onUpdatePresentation({ content: e.target.value })}
                            placeholder="Content text"
                        />
                    ) : null}

                    {selectedPresentation.type === 'illustration' || selectedPresentation.type === 'interactive-img' ? (
                        <TextInput
                            label="Image URL"
                            value={selectedPresentation.imageUrl || ''}
                            onChange={(e) => onUpdatePresentation({ imageUrl: e.target.value })}
                            placeholder="https://example.com/image.jpg"
                        />
                    ) : null}

                    {selectedPresentation.type === 'video' ? (
                        <FlexDiv column gap={8}>
                            <TextInput
                                label="Video URL"
                                value={selectedPresentation.videoUrl || ''}
                                onChange={(e) => onUpdatePresentation({ videoUrl: e.target.value })}
                                placeholder="https://example.com/video.mp4"
                            />
                            <FlexDiv alignCenter gap={8}>
                                <Checkbox
                                    checked={selectedPresentation.autoplay || false}
                                    onChange={(e) => onUpdatePresentation({ autoplay: e.target.checked })}
                                />
                                <TextDefault>Autoplay</TextDefault>
                            </FlexDiv>
                        </FlexDiv>
                    ) : null}

                    <CustomPresentationEditor
                        presentation={selectedPresentation}
                        onUpdatePresentation={onUpdatePresentation}
                    />

                    {(() => {
                        const ids = collectPresentationActionIds(
                            selectedPresentation
                        ).filter((id) =>
                            isArrayActionEntry(question.actions, id)
                        );
                        if (!ids.length) return null;
                        return (
                            <FlexDiv column gap={10}>
                                <TextDefault bold>
                                    Presentation interaction scores
                                </TextDefault>
                                <TextDefault lighter>
                                    For phishing-style steps, hover/click
                                    actionIds often use a simple score array in
                                    actions.
                                </TextDefault>
                                {ids.map((id) => (
                                    <Stack
                                        key={id}
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={2}
                                        alignItems="flex-start"
                                    >
                                        <TextField
                                            size="small"
                                            sx={{ width: { xs: '100%', sm: 200 } }}
                                            label={`Score · ${id}`}
                                            type="number"
                                            inputProps={{ step: 'any' }}
                                            value={getArrayActionScore(
                                                question.actions,
                                                id
                                            )}
                                            onChange={(e) => {
                                                if (!onUpdateQuestion) return;
                                                onUpdateQuestion({
                                                    actions: setArrayActionScore(
                                                        question.actions,
                                                        id,
                                                        e.target.value,
                                                        question
                                                    ),
                                                });
                                            }}
                                        />
                                        <Stack
                                            sx={{
                                                width: { xs: '100%', sm: 240 },
                                                flexShrink: 0,
                                            }}
                                        >
                                            <ScoringComponentSelect
                                                label={`Component · ${id}`}
                                                componentOptions={
                                                    scoringComponentOptions
                                                }
                                                value={getArrayActionComponent(
                                                    question.actions,
                                                    id,
                                                    question
                                                )}
                                                onChange={(comp) => {
                                                    if (!onUpdateQuestion) return;
                                                    onUpdateQuestion({
                                                        actions:
                                                            setArrayActionComponent(
                                                                question.actions,
                                                                id,
                                                                comp,
                                                                question
                                                            ),
                                                    });
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                ))}
                            </FlexDiv>
                        );
                    })()}
                </FlexDiv>
            </Block>
        );
    };

    const renderQuestionProperties = () => {
        const currentArea =
            question.area != null && String(question.area).trim() !== ''
                ? String(question.area).trim()
                : 'General';
        const areaChoices = threatAreaSelectValues(
            threatAreaOptions,
            currentArea
        );
        const selectAreaValue = areaChoices.includes(currentArea)
            ? currentArea
            : areaChoices[0];

        return (
            <Block padding={16} gap={16}>
                <Heading4>Question Properties</Heading4>
                
                <FlexDiv column gap={12}>
                    <TextInput
                        label="Question Name"
                        value={question.name || ''}
                        onChange={(e) => onUpdateQuestion({ name: e.target.value })}
                        placeholder="Question name"
                    />

                    {question.owner_unique_id ? (
                        <TextField
                            fullWidth
                            label="Owner (company or partner GUID)"
                            value={question.owner_unique_id}
                            InputProps={{ readOnly: true }}
                            helperText="Set by the server when you create the question or save a copy."
                        />
                    ) : null}
                    
                    <TextField
                        multiline
                        rows={3}
                        fullWidth
                        label="Description"
                        value={question.description || ''}
                        onChange={(e) => onUpdateQuestion({ description: e.target.value })}
                        placeholder="Question description"
                    />
                    
                    <FormControl fullWidth>
                        <InputLabel>Threat Area</InputLabel>
                        <Select
                            label="Threat Area"
                            value={selectAreaValue}
                            onChange={(e) => onUpdateQuestion({ area: e.target.value })}
                        >
                            {areaChoices.map((name) => (
                                <MenuItem key={name} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <FormControl fullWidth>
                        <InputLabel>Difficulty</InputLabel>
                        <Select
                            value={question.difficulty || 'basic'}
                            onChange={(e) => onUpdateQuestion({ difficulty: e.target.value })}
                        >
                            <MenuItem value="basic">Basic</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="advanced">Advanced</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>Scoring component</InputLabel>
                        <Select
                            label="Scoring component"
                            value={questionComponentsSelectValue(
                                question.components,
                                scoringComponentOptions
                            )}
                            onChange={(e) =>
                                onUpdateQuestion({ components: e.target.value })
                            }
                        >
                            {scoringComponentOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextDefault lighter>
                        Key behaviours for the selected threat area (admin →
                        Threat areas). Used as the default{' '}
                        <code>component</code> on new score rows in{' '}
                        <code>actions</code>.
                    </TextDefault>
                </FlexDiv>
            </Block>
        );
    };

    return (
        <div className="properties-panel">
            <FlexDiv column gap={16}>
                {showQuestion ? renderQuestionProperties() : null}
                {showSelection ? renderBlockProperties() : null}
                {showSelection ? renderPresentationProperties() : null}

                {showSelection &&
                !selectedBlock &&
                !selectedPresentation ? (
                    <Block padding={16}>
                        <TextDefault lighter>
                            Select a block or presentation on the canvas to edit
                            its properties.
                        </TextDefault>
                    </Block>
                ) : null}
            </FlexDiv>
        </div>
    );
}

export default PropertiesPanel;
